import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { getSocketUrl } from '../config/apiConfig';

// 🎯 INTERFACES
interface PaymentStatus {
  paymentId: string;
  status: 'approved' | 'pending' | 'cancelled' | 'rejected' | 'refunded';
  statusDetail: string;
  amount: number;
  currency: string;
  externalReference: string;
  timestamp: string;
}

interface UsePaymentListenerOptions {
  paymentId: string;
  timeout?: number;
  onPaymentComplete?: (status: PaymentStatus) => void;
  onError?: (error: string) => void;
}

// 🔌 CONFIGURACIÓN
const SOCKET_URL = getSocketUrl();

export const usePaymentListener = ({
  paymentId,
  timeout = 300000, // 5 minutos
  onPaymentComplete,
  onError
}: UsePaymentListenerOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(timeout / 1000);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const socketRef = useRef<Socket | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  // 🔌 INICIALIZAR SOCKET.IO
  useEffect(() => {
    if (!paymentId) return;

    console.log('🔌 Inicializando usePaymentListener para pago:', paymentId);
    
    // Crear conexión Socket.IO
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });

    const socket = socketRef.current;

    // 📡 EVENTOS DE CONEXIÓN
    socket.on('connect', () => {
      console.log('✅ Conectado a Socket.IO:', socket.id);
      setIsConnected(true);
      setIsLoading(false);
      setError(null);
      
      // Unirse a la sala específica del pago
      socket.emit('join-payment-room', paymentId);
      console.log(`👤 Uniéndose a sala de pago: payment-${paymentId}`);
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ Desconectado de Socket.IO:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Error de conexión Socket.IO:', error);
      setIsConnected(false);
      setIsLoading(false);
      const errorMessage = `Error de conexión: ${error.message}`;
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    });

    // 💳 EVENTOS DE PAGO
    socket.on('payment-status-update', (status: PaymentStatus) => {
      console.log('💳 Estado de pago recibido:', status);
      
      if (status.externalReference === paymentId) {
        setPaymentStatus(status);
        
        // Limpiar timeouts
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        
        // Notificar al componente padre
        if (onPaymentComplete) {
          onPaymentComplete(status);
        }
        
        // Redirigir según el estado
        // PaymentSuccess ya actualiza su estado local.
        // No volver a navegar para evitar reiniciar el componente.

      }
    });

    // 🔍 EVENTO DE DEBUG (opcional)
    socket.on('payment-webhook-received', (data) => {
      console.log('🔔 Webhook recibido:', data);
    });

    // ⏰ CONFIGURAR TIMEOUT
    timeoutRef.current = setTimeout(() => {
      console.log('⏰ Timeout alcanzado para el pago:', paymentId);
      const timeoutMessage = 'Tiempo de espera agotado. El pago puede estar pendiente.';
      setError(timeoutMessage);
      setIsLoading(false);
      if (onError) {
        onError(timeoutMessage);
      }
    }, timeout);

    // ⏱️ CONTADOR REGRESIVO
    countdownRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // 🧹 LIMPIAR AL DESMONTAR
    return () => {
      console.log('🧹 Limpiando usePaymentListener...');
      
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [paymentId, timeout, onPaymentComplete, onError]);

  // 🔄 MANEJAR REDIRECCIÓN SEGÚN ESTADO
  const handlePaymentRedirect = (status: PaymentStatus) => {
    console.log('🔄 Redirigiendo según estado:', status.status);
    
    switch (status.status) {
      case 'approved':
        navigate('/payment/success', { 
          state: { 
            paymentStatus: status,
            message: '¡Pago aprobado exitosamente!' 
          } 
        });
        break;
        
      case 'pending':
        navigate('/payment/pending', { 
          state: { 
            paymentStatus: status,
            message: 'Pago pendiente de confirmación' 
          } 
        });
        break;
        
      case 'cancelled':
      case 'rejected':
        navigate('/payment/failure', { 
          state: { 
            paymentStatus: status,
            message: 'Pago cancelado o rechazado' 
          } 
        });
        break;
        
      default:
        navigate('/payment/failure', { 
          state: { 
            paymentStatus: status,
            message: 'Estado de pago desconocido' 
          } 
        });
    }
  };

  // 🎨 FORMATEAR TIEMPO
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 🔄 RECONECTAR
  const reconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current.connect();
    }
  };

  return {
    isConnected,
    paymentStatus,
    timeRemaining,
    isLoading,
    error,
    formatTime,
    reconnect
  };
};
